// @ts-strict-ignore
import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnInit, Signal, viewChild } from '@angular/core';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { TabsetComponent } from 'ngx-bootstrap/tabs';
import { AlertHelper } from '@shared/alert/alert.helper';
import { ArticleDataService } from '@article/service/article-data.service';
import { FavoriteArticleVO } from '../../models/favorite-article.vo';
import { FavoriteArticlesOrderNavigatorContent } from './favorite-articles-order-navigator-content';
import { UserModel } from '@user/model/user.model';
import { UnusedLabelingService } from '@project/components/label/label-editor/service/unused-labeling.service';

/**
 * Component to order the favorites of the user
 */
@Component({
  standalone: false,
  templateUrl: './favorite-articles-order.component.html',
  providers: [TabsetComponent],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class FavoriteArticlesOrderComponent implements OnInit {
  private tabset: Signal<TabsetComponent> = viewChild('tabset');

  @Input() data: FavoriteArticlesOrderNavigatorContent[];

  private scrollContainer: HTMLElement;
  private rowHeight: number;

  public update = new EventEmitter<void>();

  public navigatorContents: FavoriteArticlesOrderNavigatorContent[] = [];

  public editFavoriteArticlesPermission: boolean;

  public isArticlesSortable: boolean;
  public isMoveUpOperationAvailable: boolean;
  public isMoveDownOperationAvailable: boolean;
  public selectedIndex: number = -1;

  constructor(
    private bsModalRef: BsModalRef,
    private articleDataService: ArticleDataService,
    private alertHelper: AlertHelper,
    private userModel: UserModel,
    private unusedLabelingService: UnusedLabelingService,
    ) {}

  public ngOnInit(): void {
    this.navigatorContents = this.data;
    this.editFavoriteArticlesPermission = this.userModel.user.hasPermission("EDIT_FAVORITE_ARTICLES");
  }

  public close(): void {
    this.bsModalRef.hide();
  }

  protected saveFavoriteArticles(): void {
    let favoriteArticles: FavoriteArticleVO[] = [];
    this.navigatorContents.forEach(navigatorContent => {
      if (!navigatorContent.isFunctionDefinitionType) {
        favoriteArticles = favoriteArticles.concat(navigatorContent.getSortedFavoriteArticles());
      }
    });
    this.articleDataService.updateFavoriteArticles(favoriteArticles).subscribe({
      next: (result) => this.favoriteArticlesSaved(),
      error: (error) => this.favoriteArticlesSaved_failure(),
    });
  }

  public changeTab(navigatorContent: FavoriteArticlesOrderNavigatorContent): void {
    this.selectedIndex = -1;
    this.navigatorContents.forEach(nc => nc.selectIndex(-1));
    this.changeMoveButtons(navigatorContent);
    this.update.emit();
  }

  public moveUp(): void {
    const index: number = this.tabset()?.tabs.indexOf(this.tabset()?.tabs.find(tab => tab.active));
    if (index !== -1 && index < this.navigatorContents.length) {
      const navigatorContent: FavoriteArticlesOrderNavigatorContent = this.navigatorContents[index];
      navigatorContent.moveFavoriteArticleUp();
      this.changeMoveButtons(navigatorContent);
      if (this.scrollContainer) {
        this.scrollContainer.scrollTop = this.rowHeight * navigatorContent.selectedIndex;
      }
    }
  }

  public moveDown(): void {
    const index: number = this.tabset()?.tabs.indexOf(this.tabset()?.tabs.find(tab => tab.active));
    if (index !== -1 && index < this.navigatorContents.length) {
      const navigatorContent: FavoriteArticlesOrderNavigatorContent = this.navigatorContents[index];
      navigatorContent.moveFavoriteArticleDown();
      this.changeMoveButtons(navigatorContent);
      if (this.scrollContainer) {
        if (navigatorContent.selectedIndex !== 1) {
          this.scrollContainer.scrollTop = this.rowHeight * (navigatorContent.selectedIndex - 1);
        }
      }
    }
  }

  public removeArticle(navigatorContent: FavoriteArticlesOrderNavigatorContent, article: FavoriteArticleVO): void {
    navigatorContent.removeFavoriteArticle(article);
    this.update.emit();
  }

  public selectFavoriteArticle(navigatorContent: FavoriteArticlesOrderNavigatorContent, index: number,
                               favoriteArticle: FavoriteArticleVO): void {
    this.selectedIndex = index;
    navigatorContent.selectIndex(index);
    navigatorContent.updateSortOperationsAvailability(favoriteArticle);
    this.changeMoveButtons(navigatorContent);
  }

  public setScrollInfo(scrollContainer: HTMLElement, currentRowElement: HTMLElement): void {
    this.scrollContainer = scrollContainer;
    this.rowHeight = currentRowElement.clientHeight;
  }

  private changeMoveButtons(navigatorContent: FavoriteArticlesOrderNavigatorContent): void {
    this.isArticlesSortable = navigatorContent.isArticlesSortable;
    this.isMoveUpOperationAvailable = navigatorContent.isMoveUpOperationAvailable;
    this.isMoveDownOperationAvailable = navigatorContent.isMoveDownOperationAvailable;
  }

  private favoriteArticlesSaved(): void {
    this.unusedLabelingService.favoriteArticlesUpdatedHandler();
    this.close();
  }

  private favoriteArticlesSaved_failure(): void {
    this.alertHelper.showError('favoriteArticlesOrderPopup.error.title.save_favorite_articles',
      'favoriteArticlesOrderPopup.error.message.save_favorite_articles');
    this.close();
  }
}
